/**
 * Teslyn Support access (MB decision 800; security review H1; docs/specs/cloud/support-access.md). Braynewave's platform
 * admins see a company's data only while a tenant admin of that company has granted the Teslyn Support team access
 * for a set time: "See only" or "See and change", from 1 hour to 7 days, ended early at any time. The database decides
 * (20261030130000_support_access.sql: support_access_grant, support_access_revoke, support_access_open); these are the
 * rules and words both sides of Teslyn Cloud show (the tenant admin's Company page and the support banner).
 * The team is called "Teslyn Support" in what a company reads (decision 646).
 */

/** What a grant lets the support team do (`support_access.scope`). */
export const SUPPORT_SCOPES = ['read', 'change'] as const;
export type SupportScope = (typeof SUPPORT_SCOPES)[number];

export const SUPPORT_SCOPE_LABEL: Record<SupportScope, string> = {
  read: 'See only',
  change: 'See and change',
};

export const SUPPORT_SCOPE_DETAIL: Record<SupportScope, string> = {
  read: 'The support team can open your projects, flights, reports and settings and see where your aircraft, vehicles and crew phones are, but cannot change anything.',
  change:
    'The support team can also correct records for you. They never change who belongs to your company, its sign-in roles or its integrations.',
};

export function isSupportScope(v: unknown): v is SupportScope {
  return typeof v === 'string' && (SUPPORT_SCOPES as readonly string[]).includes(v);
}

/** The longest a grant can last (the database refuses more): 7 days. */
export const SUPPORT_MAX_HOURS = 168;

/** The lengths offered when granting, in hours. */
export const SUPPORT_DURATIONS: readonly { readonly hours: number; readonly label: string }[] = [
  { hours: 4, label: '4 hours' },
  { hours: 24, label: '1 day' },
  { hours: 72, label: '3 days' },
  { hours: 168, label: '7 days' },
];

/** A grant as the database keeps it (`support_access`). */
export interface SupportGrant {
  readonly id: string;
  readonly tenant_id: string;
  readonly scope: SupportScope;
  readonly starts_at: string;
  readonly ends_at: string;
  readonly reason: string;
  readonly granted_by: string | null;
  readonly granted_at: string;
  readonly revoked_at: string | null;
  readonly revoked_by: string | null;
  readonly first_used_at: string | null;
}

export type SupportGrantState = 'upcoming' | 'active' | 'ended' | 'revoked';

/** Where a grant stands at `now`: not started yet, in force, run out, or ended early. */
export function supportGrantState(
  g: Pick<SupportGrant, 'starts_at' | 'ends_at' | 'revoked_at'>,
  now: Date,
): SupportGrantState {
  if (g.revoked_at !== null) return 'revoked';
  const t = now.getTime();
  if (t < Date.parse(g.starts_at)) return 'upcoming';
  if (t < Date.parse(g.ends_at)) return 'active';
  return 'ended';
}

export const SUPPORT_STATE_LABEL: Record<SupportGrantState, string> = {
  upcoming: 'Starts later',
  active: 'In force',
  ended: 'Ended',
  revoked: 'Ended early',
};

/** The grant in force at `now` (the latest ending, "See and change" before "See only"), or null. */
export function supportGrantInForce<
  T extends Pick<SupportGrant, 'scope' | 'starts_at' | 'ends_at' | 'revoked_at'>,
>(grants: readonly T[], now: Date): T | null {
  const live = grants.filter((g) => supportGrantState(g, now) === 'active');
  live.sort(
    (a, b) =>
      Number(b.scope === 'change') - Number(a.scope === 'change') ||
      Date.parse(b.ends_at) - Date.parse(a.ends_at),
  );
  return live[0] ?? null;
}

/** The checks a grant passes before it is sent (the database checks them again). */
export function checkSupportGrant(input: {
  readonly scope: unknown;
  readonly hours: unknown;
  readonly reason: string;
}): string | null {
  if (!isSupportScope(input.scope)) return 'Choose "See only" or "See and change".';
  const h = input.hours;
  if (typeof h !== 'number' || !Number.isInteger(h) || h < 1 || h > SUPPORT_MAX_HOURS)
    return 'Support access lasts from 1 hour to 7 days.';
  if (input.reason.trim().length > 500) return 'The reason is at most 500 characters.';
  return null;
}

/** The audit actions of support access (`audit_event.action`), newest kinds first in the record. */
export const SUPPORT_ACTIONS = [
  'support.granted',
  'support.revoked',
  'support.opened',
  'support.viewed',
  'support.changed',
] as const;
export type SupportAction = (typeof SUPPORT_ACTIONS)[number];

/** Teslyn Cloud's page names as the record shows them (`support.viewed`'s target is the page id). */
const PAGE_WORDS: Record<string, string> = {
  home: 'Home',
  projects: 'Projects',
  reports: 'Reports',
  deliverables: 'Deliverables',
  ops: 'Operations Hub',
  tracking: 'Live tracking',
  alerts: 'Alerts',
  stations: 'Base stations',
  spaceweather: 'Space weather',
  'hazard-packs': 'Hazard packs',
  'safety-signoff': 'Safety sign-off',
  'safety-register': 'Safety register',
  clients: 'Clients',
  quotes: 'Quotes',
  contracts: 'Contracts',
  people: 'People',
  aircraft: 'Aircraft',
  company: 'Company',
  integrations: 'Integrations',
};

const fmtTime = (iso: unknown): string => {
  const t = typeof iso === 'string' ? Date.parse(iso) : NaN;
  return Number.isFinite(t)
    ? new Date(t).toLocaleString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'an unknown time';
};

/** One line of the record a tenant admin reads, for an audit row of support access. */
export function supportActionWords(action: string, target: string, detail: Record<string, unknown>): string {
  const scope = isSupportScope(detail.scope) ? SUPPORT_SCOPE_LABEL[detail.scope].toLowerCase() : 'access';
  switch (action) {
    case 'support.granted':
      return `Granted Teslyn Support access (${scope}) until ${fmtTime(detail.ends_at)}${
        typeof detail.reason === 'string' && detail.reason ? `: ${detail.reason}` : ''
      }.`;
    case 'support.revoked':
      return detail.by_platform_admin === true
        ? 'Teslyn Support ended its access early.'
        : `Ended Teslyn Support access (${scope}) early.`;
    case 'support.opened':
      return `Teslyn Support opened your data (${scope}).`;
    case 'support.viewed': {
      const page = typeof detail.page === 'string' ? detail.page : target;
      return `Teslyn Support viewed ${PAGE_WORDS[page.split(':')[0] ?? ''] ?? page}.`;
    }
    case 'support.changed': {
      const table = typeof detail.table === 'string' ? detail.table.replace(/_/g, ' ') : 'a record';
      const op =
        detail.operation === 'insert' ? 'added' : detail.operation === 'delete' ? 'deleted' : 'changed';
      return `Teslyn Support ${op} a ${table} record.`;
    }
    default:
      return action;
  }
}

/** The banner a platform admin sees while working in a company's data. */
export function supportBannerWords(tenantName: string, scope: SupportScope, endsAt: string | null): string {
  const until = endsAt ? ` until ${fmtTime(endsAt)}` : '';
  return `Teslyn Support access to ${tenantName}: ${SUPPORT_SCOPE_LABEL[scope].toLowerCase()}${until}. ${tenantName}'s tenant admins see every page you open and every change you make.`;
}
